import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// ---- 401 → refresh → retry interceptor --------------------------------
// On a 401 from any protected route, attempt ONE refresh of the access
// cookie and replay the original request. If refresh itself 401s, the
// caller's ProtectedRoute will pick up `user === null` from /me and
// redirect to /login. This keeps the UX seamless across access-token
// expiry (15 min default) without ever requiring the user to re-auth.
let _refreshInFlight = null;
api.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    const status = err?.response?.status;
    const original = err?.config;
    // Don't loop on the refresh endpoint itself, or on /auth/login which
    // legitimately 401s when credentials are wrong.
    const isAuthEndpoint =
      original?.url?.includes("/auth/login") ||
      original?.url?.includes("/auth/register") ||
      original?.url?.includes("/auth/refresh");
    if (status !== 401 || !original || original._retry || isAuthEndpoint) {
      throw err;
    }
    original._retry = true;
    try {
      _refreshInFlight = _refreshInFlight ||
        api.post("/auth/refresh", {}).finally(() => {
          _refreshInFlight = null;
        });
      await _refreshInFlight;
      return api.request(original);
    } catch {
      throw err;
    }
  },
);

// Agents / Types
export const fetchAgents = () => api.get("/agents").then(r => r.data);
export const fetchAgent = (id) => api.get(`/agents/${id}`).then(r => r.data);
export const fetchDeliverableTypes = () => api.get("/deliverable-types").then(r => r.data);

// Sessions
export const createSession = (agent_id, title, workspace_id) =>
  api.post("/sessions", { agent_id, title, workspace_id }).then(r => r.data);
export const listSessions = (params = {}) =>
  api.get("/sessions", { params }).then(r => r.data);
export const getSession = (id) => api.get(`/sessions/${id}`).then(r => r.data);
export const updateSession = (id, payload) => api.patch(`/sessions/${id}`, payload).then(r => r.data);
export const deleteSession = (id) => api.delete(`/sessions/${id}`).then(r => r.data);
export const postMessage = (sessionId, content, attachments) =>
  api.post(`/sessions/${sessionId}/messages`, { content, attachments }).then(r => r.data);

// Streaming chat — uses fetch + ReadableStream
export async function* streamMessage(sessionId, content, attachments) {
  const res = await fetch(`${API_BASE}/sessions/${sessionId}/messages/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Accept": "text/event-stream" },
    body: JSON.stringify({ content, attachments }),
  });
  yield* _readSseStream(res);
}

// Resume a truncated assistant message — continues from where it left off
export async function* resumeMessage(sessionId, messageId) {
  const res = await fetch(
    `${API_BASE}/sessions/${sessionId}/messages/${messageId}/resume`,
    { method: "POST", headers: { "Accept": "text/event-stream" } }
  );
  yield* _readSseStream(res);
}

async function* _readSseStream(res) {
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Stream failed (${res.status})`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE events are separated by \n\n
    let idx;
    while ((idx = buffer.indexOf("\n\n")) >= 0) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const lines = raw.split("\n");
      let event = "message";
      let data = "";
      let isComment = false;
      for (const ln of lines) {
        if (ln.startsWith(":")) { isComment = true; continue; }
        if (ln.startsWith("event: ")) event = ln.slice(7).trim();
        else if (ln.startsWith("data: ")) data += ln.slice(6);
      }
      if (isComment && !event && !data) continue;
      if (data) {
        try { yield { event, data: JSON.parse(data) }; }
        catch { yield { event, data }; }
      }
    }
  }
}

// Deliverables
export const createDeliverable = (payload) => api.post("/deliverables", payload).then(r => r.data);
export const listDeliverables = (params = {}) => api.get("/deliverables", { params }).then(r => r.data);
export const getDeliverable = (id) => api.get(`/deliverables/${id}`).then(r => r.data);
export const updateDeliverable = (id, payload) => api.patch(`/deliverables/${id}`, payload).then(r => r.data);
export const deleteDeliverable = (id) => api.delete(`/deliverables/${id}`).then(r => r.data);

// Workspaces
export const createWorkspace = (payload) => api.post("/workspaces", payload).then(r => r.data);
export const listWorkspaces = () => api.get("/workspaces").then(r => r.data);
export const getWorkspace = (id) => api.get(`/workspaces/${id}`).then(r => r.data);
export const updateWorkspace = (id, payload) => api.patch(`/workspaces/${id}`, payload).then(r => r.data);
export const deleteWorkspace = (id) => api.delete(`/workspaces/${id}`).then(r => r.data);
export const getWorkspaceByShareToken = (token) => api.get(`/workspaces/share/${token}`).then(r => r.data);
export const promoteWorkspace = (id, audience_note = "") =>
  api.post(`/workspaces/${id}/promote`, { audience_note }).then(r => r.data);

// Workspace engagement events (fire-and-forget; never throws to the caller)
export const listWorkspaceEvents = (id, limit = 50) =>
  api.get(`/workspaces/${id}/events`, { params: { limit } }).then(r => r.data);

export function logWorkspaceEvent(id, type, metadata) {
  if (!id || !type) return Promise.resolve(null);
  return api
    .post(`/workspaces/${id}/events`, { type, metadata: metadata || {} })
    .then((r) => r.data)
    .catch(() => null);
}

// Telemetry (system-level events, not tied to a workspace). Fire-and-forget.
export function logTelemetryEvent(type, metadata) {
  if (!type) return Promise.resolve(null);
  return api
    .post(`/telemetry/event`, { type, metadata: metadata || {} })
    .then((r) => r.data)
    .catch(() => null);
}

export const getPipelineVelocity = (days = 7) =>
  api.get(`/telemetry/pipeline-velocity`, { params: { days } })
    .then((r) => r.data)
    .catch(() => null);

export const getMetadataCounts = (workspaceId) =>
  api.get(`/workspaces/${workspaceId}/metadata/counts`).then((r) => r.data);

export const getMetadataDashboard = (workspaceId) =>
  api.get(`/workspaces/${workspaceId}/metadata/dashboard`).then((r) => r.data);

// Iter 36.e — Top write-churn rollup derived from imported DSPJRN/DSPJRNRCV.
// Auto-hidden on the dashboard when totals.total_entries === 0.
export const getJournalRollup = (workspaceId, { topN = 10 } = {}) =>
  api
    .get(`/workspaces/${workspaceId}/metadata/journal-rollup`, { params: { top_n: topN } })
    .then((r) => r.data);

// Iter 36.f — Decommission radar. Files referenced by programs but never
// written in the imported journal window. Returns has_journal_window=false
// when no DSPJRN slice exists yet — the panel renders a different prompt
// in that case.
export const getDecommissionRadar = (workspaceId, { limit = 50 } = {}) =>
  api
    .get(`/workspaces/${workspaceId}/metadata/decommission-radar`, { params: { limit } })
    .then((r) => r.data);

// Iter 36.g — Steering-committee bundle PDF. Returns the full URL so the
// frontend can `window.open()` it (browser handles auth via cookies + the
// download Content-Disposition header).
export const getSteeringBundlePdfUrl = (workspaceId) =>
  `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/steering-bundle.pdf`;

// Iter 36.h — Email the steering-committee bundle. Same recipient shape as
// sendRiskBriefEmail (member quick-pick OR free-form email + optional note).
// The bundle PDF is server-built and attached to the message automatically.
export const sendSteeringBundleEmail = (
  workspaceId,
  { recipientUserId, recipientEmail, personalMessage } = {},
) =>
  api
    .post(`/workspaces/${encodeURIComponent(workspaceId)}/steering-bundle/email`, {
      ...(recipientUserId ? { recipient_user_id: recipientUserId } : {}),
      ...(recipientEmail ? { recipient_email: recipientEmail } : {}),
      ...(personalMessage ? { personal_message: personalMessage } : {}),
    })
    .then((r) => r.data);

// Iter 36.i — Scheduled bundle emails. GET returns {schedule: doc | null}.
// PUT upserts (200 + saved doc). DELETE clears (200 + {deleted: bool}).
export const getBundleSchedule = (workspaceId) =>
  api
    .get(`/workspaces/${encodeURIComponent(workspaceId)}/steering-bundle/schedule`)
    .then((r) => r.data);

export const putBundleSchedule = (workspaceId, payload) =>
  api
    .put(`/workspaces/${encodeURIComponent(workspaceId)}/steering-bundle/schedule`, payload)
    .then((r) => r.data);

export const deleteBundleSchedule = (workspaceId) =>
  api
    .delete(`/workspaces/${encodeURIComponent(workspaceId)}/steering-bundle/schedule`)
    .then((r) => r.data);

// ---- Iter 36.j — Public steering-bundle snapshot links --------------
export const createBundleSnapshot = (
  workspaceId,
  { recipientEmail, recipientLabel, label, ttlDays } = {},
) =>
  api
    .post(
      `/workspaces/${encodeURIComponent(workspaceId)}/steering-bundle/snapshots`,
      {
        ...(recipientEmail ? { recipient_email: recipientEmail } : {}),
        ...(recipientLabel ? { recipient_label: recipientLabel } : {}),
        ...(label ? { label } : {}),
        ...(ttlDays ? { ttl_days: ttlDays } : {}),
      },
    )
    .then((r) => r.data);

export const listBundleSnapshots = (workspaceId) =>
  api
    .get(`/workspaces/${encodeURIComponent(workspaceId)}/steering-bundle/snapshots`)
    .then((r) => r.data.snapshots);

export const revokeBundleSnapshot = (workspaceId, token) =>
  api
    .delete(
      `/workspaces/${encodeURIComponent(workspaceId)}/steering-bundle/snapshots/${encodeURIComponent(token)}`,
    )
    .then((r) => r.data);

// Public (unauthenticated) snapshot endpoints — bypass the authenticated
// axios instance so the public viewer doesn't ship credentials.
export const getBundleSnapshot = (token) => {
  const base = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
  return fetch(`${base}/api/share/bundle/${encodeURIComponent(token)}`).then((r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });
};

export const getBundleSnapshotInfo = (token) => {
  const base = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
  return fetch(`${base}/api/share/bundle/${encodeURIComponent(token)}/info`).then((r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });
};

export const bundleSnapshotPdfUrl = (token) => {
  const base = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
  return `${base}/api/share/bundle/${encodeURIComponent(token)}/pdf`;
};

// ---- Iter 36.l — Cross-workspace snapshot pulse ---------------------
export const getSnapshotPulse = (lookbackDays = 7) =>
  api
    .get(`/snapshot-pulse/me`, { params: { lookback_days: lookbackDays } })
    .then((r) => r.data);

export const sendSnapshotPulse = (lookbackDays = 7) =>
  api
    .post(`/snapshot-pulse/me/send`, null, { params: { lookback_days: lookbackDays } })
    .then((r) => r.data);

export const getModernizationOpportunities = (workspaceId) =>
  api.get(`/workspaces/${workspaceId}/metadata/opportunities`).then((r) => r.data);

export const narrateModernizationOpportunities = (workspaceId, { force = false } = {}) =>
  api
    .post(`/workspaces/${workspaceId}/metadata/opportunities/narrate`, null, {
      timeout: 120000,
      params: force ? { force: true } : {},
    })
    .then((r) => r.data);

export const getImpactGraph = (workspaceId, { object, library, depth = 2, maxNodes = 80, includeFields = false, maxFields = 60 } = {}) =>
  api
    .get(`/workspaces/${workspaceId}/metadata/impact`, {
      params: {
        object,
        ...(library ? { library } : {}),
        depth,
        max_nodes: maxNodes,
        ...(includeFields ? { include_fields: true, max_fields: maxFields } : {}),
      },
    })
    .then((r) => r.data);

export const listMetadataBackups = () =>
  api.get(`/metadata/backups`).then((r) => r.data);

export const triggerMetadataBackup = () =>
  api.post(`/metadata/backups`).then((r) => r.data);

export const restoreMetadataBackup = (name) =>
  api.post(`/metadata/backups/${encodeURIComponent(name)}/restore`).then((r) => r.data);

export const listMetadataEntity = (workspaceId, entity, { limit = 100, offset = 0, q } = {}) =>
  api
    .get(`/workspaces/${workspaceId}/metadata/${entity}`, {
      params: { limit, offset, ...(q ? { q } : {}) },
    })
    .then((r) => r.data);

export const truncateMetadataEntity = (workspaceId, entity) =>
  api.delete(`/workspaces/${workspaceId}/metadata/${entity}`).then((r) => r.data);

export function importMetadataCsv(workspaceId, entity, file, { upsert = true, force = false, capturedAt } = {}) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("upsert", String(upsert));
  return api
    .post(`/workspaces/${workspaceId}/metadata/${entity}/import`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      params: { upsert, force, ...(capturedAt ? { captured_at: capturedAt } : {}) },
    })
    .then((r) => r.data);
}

export function importMetadataBulkZip(workspaceId, file, { upsert = true, force = false, capturedAt } = {}) {
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post(`/workspaces/${workspaceId}/metadata/import/bulk`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      params: { upsert, force, ...(capturedAt ? { captured_at: capturedAt } : {}) },
      // Bulk imports of large catalogs can take a while — bump timeout.
      timeout: 300000,
    })
    .then((r) => r.data);
}

// ---- Iter 38 — Evidence Provenance Layer v1 ----------------------------
export const listImportBatches = (workspaceId, { sourceCommand, status, entity, limit = 100, offset = 0 } = {}) =>
  api
    .get(`/workspaces/${workspaceId}/import-batches`, {
      params: {
        limit,
        offset,
        ...(sourceCommand ? { source_command: sourceCommand } : {}),
        ...(status ? { status } : {}),
        ...(entity ? { entity } : {}),
      },
    })
    .then((r) => r.data);

export const getImportBatch = (workspaceId, batchId) =>
  api.get(`/workspaces/${workspaceId}/import-batches/${batchId}`).then((r) => r.data);

export const getEvidenceSummary = (workspaceId) =>
  api.get(`/workspaces/${workspaceId}/import-batches/summary`).then((r) => r.data);

// ---- Iter 39 — Disposition & Treatment Matrix v1 ------------------------
export const listDispositions = (workspaceId, { state, treatment, disposition, kind, signal, stale, q, limit = 500, offset = 0 } = {}) =>
  api
    .get(`/workspaces/${workspaceId}/dispositions`, {
      params: {
        limit,
        offset,
        ...(state ? { state } : {}),
        ...(treatment ? { treatment } : {}),
        ...(disposition ? { disposition } : {}),
        ...(kind ? { kind } : {}),
        ...(signal ? { signal } : {}),
        ...(stale ? { stale } : {}),
        ...(q ? { q } : {}),
      },
    })
    .then((r) => r.data);

export const getDispositionSummary = (workspaceId) =>
  api.get(`/workspaces/${workspaceId}/dispositions/summary`).then((r) => r.data);

export const seedDispositions = (workspaceId) =>
  api.post(`/workspaces/${workspaceId}/dispositions/seed`).then((r) => r.data);

export const createDisposition = (workspaceId, body) =>
  api.post(`/workspaces/${workspaceId}/dispositions`, body).then((r) => r.data);

export const getDisposition = (workspaceId, dispositionId) =>
  api.get(`/workspaces/${workspaceId}/dispositions/${dispositionId}`).then((r) => r.data);

export const patchDisposition = (workspaceId, dispositionId, body) =>
  api.patch(`/workspaces/${workspaceId}/dispositions/${dispositionId}`, body).then((r) => r.data);

export const decideDisposition = (workspaceId, dispositionId, decision, note) =>
  api
    .post(`/workspaces/${workspaceId}/dispositions/${dispositionId}/decision`, { decision, ...(note != null ? { note } : {}) })
    .then((r) => r.data);

// Iter 51 — bulk validate / reject / reopen a matrix cell or a filtered selection (dry_run previews).
export const bulkDisposition = (workspaceId, body) =>
  api.post(`/workspaces/${workspaceId}/dispositions/bulk-decision`, body).then((r) => r.data);

// ---- Cross-workspace knowledge base ------------------------------------
export const getFindingsOverview = () =>
  api.get("/findings/overview").then((r) => r.data);

export const getFindingsRepeatedNames = ({ minWorkspaces = 2, limit = 100 } = {}) =>
  api
    .get("/findings/repeated-names", { params: { min_workspaces: minWorkspaces, limit } })
    .then((r) => r.data);

export const getFindingsPatterns = () =>
  api.get("/findings/patterns").then((r) => r.data);

export const getFindingOccurrences = (fileName) =>
  api.get(`/findings/by-name/${encodeURIComponent(fileName)}`).then((r) => r.data);

// ---- Workspace membership (iter 25) ------------------------------------
export const listWorkspaceMembers = (workspaceId) =>
  api.get(`/workspaces/${workspaceId}/members`).then((r) => r.data);

export const inviteWorkspaceMember = (workspaceId, email, role) =>
  api
    .post(`/workspaces/${workspaceId}/members/invite`, { email, role })
    .then((r) => r.data);

export const updateWorkspaceMemberRole = (workspaceId, userId, role) =>
  api
    .patch(`/workspaces/${workspaceId}/members/${userId}`, { role })
    .then((r) => r.data);

export const removeWorkspaceMember = (workspaceId, userId) =>
  api
    .delete(`/workspaces/${workspaceId}/members/${userId}`)
    .then((r) => r.data);

export const revokeWorkspaceInvite = (workspaceId, email) =>
  api
    .delete(`/workspaces/${workspaceId}/invites`, { params: { email } })
    .then((r) => r.data);

export const previewWorkspaceInvite = (token) =>
  api.get(`/workspaces/invites/preview`, { params: { token } }).then((r) => r.data);

export const acceptWorkspaceInvite = (payload) =>
  api.post(`/workspaces/invites/accept`, payload).then((r) => r.data);

// ---- Cross-workspace activity feed (iter 25) ---------------------------
export const getActivityFeed = ({ limit = 20, workspaceId } = {}) =>
  api
    .get("/activity", {
      params: { limit, ...(workspaceId ? { workspace_id: workspaceId } : {}) },
    })
    .then((r) => r.data);

// ---- Source parser (iter 26) -------------------------------------------
export const parseSingleSource = (workspaceId, payload) =>
  api
    .post(`/workspaces/${workspaceId}/source-parser/parse`, payload)
    .then((r) => r.data);

export const parseBulkZip = (workspaceId, file, onUploadProgress) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post(`/workspaces/${workspaceId}/source-parser/bulk-zip`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
      timeout: 120_000, // bulk parsing can take a moment
    })
    .then((r) => r.data);
};

export const listParsedPrograms = (workspaceId) =>
  api
    .get(`/workspaces/${workspaceId}/source-parser/programs`)
    .then((r) => r.data);

export const getParsedProgram = (workspaceId, programName) =>
  api
    .get(`/workspaces/${workspaceId}/source-parser/programs/${encodeURIComponent(programName)}`)
    .then((r) => r.data);

export const deleteParsedProgram = (workspaceId, programName) =>
  api
    .delete(`/workspaces/${workspaceId}/source-parser/programs/${encodeURIComponent(programName)}`)
    .then((r) => r.data);

export const getParserIoMatrix = (workspaceId) =>
  api
    .get(`/workspaces/${workspaceId}/source-parser/io-matrix`)
    .then((r) => r.data);

// ---- SAP ECC on IBM i (iter 27) ----------------------------------------
export const importSapExtract = (workspaceId, file, onUploadProgress) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post(`/workspaces/${workspaceId}/sap-ecc/import`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
      timeout: 60_000,
    })
    .then((r) => r.data);
};

export const importSapBulkZip = (workspaceId, file, onUploadProgress) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post(`/workspaces/${workspaceId}/sap-ecc/bulk-zip`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
      timeout: 120_000,
    })
    .then((r) => r.data);
};

export const getSapOverview = (workspaceId) =>
  api.get(`/workspaces/${workspaceId}/sap-ecc/overview`).then((r) => r.data);

export const listSapComponents = (workspaceId) =>
  api.get(`/workspaces/${workspaceId}/sap-ecc/components`).then((r) => r.data);

export const listSapPlatform = (workspaceId) =>
  api.get(`/workspaces/${workspaceId}/sap-ecc/platform`).then((r) => r.data);

export const listSapTables = (workspaceId, { limit = 200, offset = 0, linkedOnly = false } = {}) =>
  api
    .get(`/workspaces/${workspaceId}/sap-ecc/tables`, {
      params: { limit, offset, linked_only: linkedOnly },
    })
    .then((r) => r.data);

export const sapTemplateUrl = (workspaceId, key) => {
  const base = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
  return `${base}/api/workspaces/${workspaceId}/sap-ecc/templates/${key}.csv`;
};

export const getSapPtfHealth = (workspaceId) =>
  api.get(`/workspaces/${workspaceId}/sap-ecc/ptf-health`).then((r) => r.data);

// ---- SAP ECC ABAP inventory (iter 28) ----------------------------------
export const listAbapObjects = (
  workspaceId,
  {
    limit = 200,
    offset = 0,
    objType,
    pkg,
    namespace,
    customOnly = false,
    modifiedOnly = false,
    q,
  } = {},
) =>
  api
    .get(`/workspaces/${workspaceId}/sap-ecc/abap-objects`, {
      params: {
        limit,
        offset,
        ...(objType ? { obj_type: objType } : {}),
        ...(pkg ? { package: pkg } : {}),
        ...(namespace ? { namespace } : {}),
        ...(customOnly ? { custom_only: true } : {}),
        ...(modifiedOnly ? { modified_only: true } : {}),
        ...(q ? { q } : {}),
      },
    })
    .then((r) => r.data);

export const getAbapSummary = (workspaceId) =>
  api.get(`/workspaces/${workspaceId}/sap-ecc/abap-summary`).then((r) => r.data);

// ---- SAP ECC ABAP Heat Map deliverable (iter 29) ----------------------
export const getAbapHeatmapData = (workspaceId) =>
  api.get(`/workspaces/${workspaceId}/sap-ecc/heatmap/data`).then((r) => r.data);

export const generateAbapHeatmap = (workspaceId, { title, context } = {}) =>
  api
    .post(`/workspaces/${workspaceId}/sap-ecc/heatmap/generate`, {
      ...(title ? { title } : {}),
      ...(context ? { context } : {}),
    })
    .then((r) => r.data);

/**
 * Heat-map PDF download URL (iter 30). Returns a *plain URL string* not a
 * fetch promise so the caller can use a standard `<a href=...>` download —
 * which is the correct UX for file downloads (browser-managed progress + the
 * Save-As dialog respects the server-provided Content-Disposition filename).
 * Auth flows automatically via the HTTP-only cookie the browser already sends
 * on same-domain `<a href>` clicks.
 */
export const abapHeatmapPdfUrl = (workspaceId, { deliverableId } = {}) => {
  const base = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
  const url = `${base}/api/workspaces/${workspaceId}/sap-ecc/heatmap/pdf`;
  return deliverableId ? `${url}?deliverable_id=${encodeURIComponent(deliverableId)}` : url;
};

// ---- Iter 31 — Public heat-map share tokens ---------------------------
export const createHeatmapShare = (workspaceId, { recipientEmail, recipientLabel, ttlDays, deliverableId } = {}) =>
  api
    .post(`/workspaces/${workspaceId}/sap-ecc/heatmap/shares`, {
      ...(recipientEmail ? { recipient_email: recipientEmail } : {}),
      ...(recipientLabel ? { recipient_label: recipientLabel } : {}),
      ...(ttlDays ? { ttl_days: ttlDays } : {}),
      ...(deliverableId ? { deliverable_id: deliverableId } : {}),
    })
    .then((r) => r.data);

export const listHeatmapShares = (workspaceId) =>
  api.get(`/workspaces/${workspaceId}/sap-ecc/heatmap/shares`).then((r) => r.data.shares);

export const revokeHeatmapShare = (workspaceId, token) =>
  api.delete(`/workspaces/${workspaceId}/sap-ecc/heatmap/shares/${encodeURIComponent(token)}`).then((r) => r.data);

// Iter 34 — one-click nudge to remind unopened share recipients.
export const nudgeShareRecipient = (workspaceId, token, { customMessage } = {}) =>
  api
    .post(
      `/workspaces/${workspaceId}/sap-ecc/heatmap/shares/${encodeURIComponent(token)}/nudge`,
      customMessage ? { custom_message: customMessage } : {},
    )
    .then((r) => r.data);

// Public (unauthenticated) share viewer endpoints — these are called
// directly from the public /share/heatmap/:token route, so they go through
// a fresh axios instance with no auth cookies / interceptors.
export const getHeatmapShare = (token) => {
  const base = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
  return fetch(`${base}/api/share/heatmap/${encodeURIComponent(token)}`).then((r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });
};

export const getHeatmapShareInfo = (token) => {
  const base = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
  return fetch(`${base}/api/share/heatmap/${encodeURIComponent(token)}/info`).then((r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });
};

export const heatmapSharePdfUrl = (token) => {
  const base = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
  return `${base}/api/share/heatmap/${encodeURIComponent(token)}/pdf`;
};

// ---- Iter 33 — Weekly consultant digest -------------------------------
export const getMyDigestPreview = (lookbackDays = 7) =>
  api
    .get("/digests/me/preview", { params: { lookback_days: lookbackDays } })
    .then((r) => r.data);

export const sendMyDigest = (lookbackDays = 7) =>
  api
    .post("/digests/me/send", null, { params: { lookback_days: lookbackDays } })
    .then((r) => r.data);

// Iter 34.b — bulk-nudge every chase candidate in the digest.
export const nudgeChaseCandidates = (lookbackDays = 7) =>
  api
    .post("/digests/me/nudge-chase", null, { params: { lookback_days: lookbackDays } })
    .then((r) => r.data);

// Iter 34.d — snooze (or unsnooze with days=0) a heatmap share so it stops
// appearing in chase candidates / bulk nudge sweeps.
export const snoozeShare = (workspaceId, token, days) =>
  api
    .patch(
      `/workspaces/${encodeURIComponent(workspaceId)}/sap-ecc/heatmap/shares/${encodeURIComponent(token)}/snooze`,
      { days },
    )
    .then((r) => r.data);

// Iter 35 — SMOD/CMOD enhancement-point inventory + rollup.
export const listEnhancements = (workspaceId, params = {}) =>
  api
    .get(`/workspaces/${encodeURIComponent(workspaceId)}/sap-ecc/enhancements`, { params })
    .then((r) => r.data);
export const getEnhancementsSummary = (workspaceId) =>
  api
    .get(`/workspaces/${encodeURIComponent(workspaceId)}/sap-ecc/enhancements/summary`)
    .then((r) => r.data);

// Iter 36 — Business-Rule Discovery: walk parsed RPG for IF/WHEN/CASE/DOW/DOU.
export const listBusinessRules = (workspaceId, params = {}) =>
  api
    .get(`/workspaces/${encodeURIComponent(workspaceId)}/business-rules`, { params })
    .then((r) => r.data);
export const getBusinessRuleClusters = (workspaceId) =>
  api
    .get(`/workspaces/${encodeURIComponent(workspaceId)}/business-rules/clusters`)
    .then((r) => r.data);
export const rebuildBusinessRules = (workspaceId) =>
  api
    .post(`/workspaces/${encodeURIComponent(workspaceId)}/business-rules/rebuild`)
    .then((r) => r.data);

// Iter 40 — Rule Validation State: consultant validates / rejects / reopens inference.
export const decideBusinessRule = (workspaceId, ruleId, decision, note) =>
  api
    .post(`/workspaces/${encodeURIComponent(workspaceId)}/business-rules/${ruleId}/decision`,
      { decision, ...(note != null ? { note } : {}) })
    .then((r) => r.data);
export const bulkDecideBusinessRules = (workspaceId, ids, decision, note) =>
  api
    .post(`/workspaces/${encodeURIComponent(workspaceId)}/business-rules/bulk-decision`,
      { ids, decision, ...(note != null ? { note } : {}) })
    .then((r) => r.data);
export const decideDeliverable = (deliverableId, decision, note) =>
  api
    .post(`/deliverables/${encodeURIComponent(deliverableId)}/decision`,
      { decision, ...(note != null ? { note } : {}) })
    .then((r) => r.data);
export const getFindingsBusinessRules = ({ includeUnvalidated = false, limit = 200 } = {}) =>
  api
    .get("/findings/business-rules", { params: { include_unvalidated: includeUnvalidated, limit } })
    .then((r) => r.data);

// Iter 36.b — workspace risk brief (top-10 stuck items for Steering-Committee slide).
export const getRiskBrief = (workspaceId) =>
  api
    .get(`/workspaces/${encodeURIComponent(workspaceId)}/risk-brief`)
    .then((r) => r.data);

// Iter 36.c — email the brief to a teammate or external address.
// One of recipientUserId or recipientEmail must be passed. personalMessage
// is an optional plain-text callout rendered above the brief tables.
export const sendRiskBriefEmail = (
  workspaceId,
  { recipientUserId, recipientEmail, personalMessage } = {},
) =>
  api
    .post(`/workspaces/${encodeURIComponent(workspaceId)}/risk-brief/email`, {
      ...(recipientUserId ? { recipient_user_id: recipientUserId } : {}),
      ...(recipientEmail ? { recipient_email: recipientEmail } : {}),
      ...(personalMessage ? { personal_message: personalMessage } : {}),
    })
    .then((r) => r.data);

// ---- Password reset (iter 25) ------------------------------------------
export const forgotPassword = (email) =>
  api.post(`/auth/forgot-password`, { email }).then((r) => r.data);

export const resetPassword = (token, new_password) =>
  api.post(`/auth/reset-password`, { token, new_password }).then((r) => r.data);

// Iter 41 — Field-Level Dependencies (deterministic field references).
const _wsPath = (workspaceId) => `/workspaces/${encodeURIComponent(workspaceId)}`;
export const listFieldReferences = (workspaceId, params = {}) =>
  api.get(`${_wsPath(workspaceId)}/field-references`, { params }).then((r) => r.data);
export const getFieldReferenceSummary = (workspaceId) =>
  api.get(`${_wsPath(workspaceId)}/field-references/summary`).then((r) => r.data);
export const getFieldReference = (workspaceId, referenceId) =>
  api.get(`${_wsPath(workspaceId)}/field-references/${referenceId}`).then((r) => r.data);
export const rebuildFieldReferences = (workspaceId) =>
  api.post(`${_wsPath(workspaceId)}/field-references/rebuild`).then((r) => r.data);
export const createManualFieldReference = (workspaceId, body) =>
  api.post(`${_wsPath(workspaceId)}/field-references`, body).then((r) => r.data);
export const getFieldImpact = (workspaceId, { field, file, library, limit = 200 } = {}) =>
  api
    .get(`${_wsPath(workspaceId)}/metadata/field-impact`, {
      params: { field, ...(file ? { file } : {}), ...(library ? { library } : {}), limit },
    })
    .then((r) => r.data);

// ---- Screen format bindings (Iter 45) ----
export const listScreenBindings = (workspaceId, params = {}) =>
  api.get(`${_wsPath(workspaceId)}/screen-format-bindings`, { params }).then((r) => r.data);
export const getScreenBinding = (workspaceId, bindingId) =>
  api.get(`${_wsPath(workspaceId)}/screen-format-bindings/${bindingId}`).then((r) => r.data);
export const rebuildScreenBindings = (workspaceId) =>
  api.post(`${_wsPath(workspaceId)}/screen-format-bindings/rebuild`).then((r) => r.data);
// Iter 48 — manual bindings (consultant supplied, not parser-proven)
export const createScreenBinding = (workspaceId, body) =>
  api.post(`${_wsPath(workspaceId)}/screen-format-bindings`, body).then((r) => r.data);
export const updateScreenBinding = (workspaceId, bindingId, body) =>
  api.patch(`${_wsPath(workspaceId)}/screen-format-bindings/${bindingId}`, body).then((r) => r.data);
export const deactivateScreenBinding = (workspaceId, bindingId) =>
  api.delete(`${_wsPath(workspaceId)}/screen-format-bindings/${bindingId}`).then((r) => r.data);

// ---- Screen walkthrough (Iter 47) ----
export const listScreenWalkthroughs = (workspaceId, params = {}) =>
  api.get(`${_wsPath(workspaceId)}/screen-walkthroughs`, { params }).then((r) => r.data);
export const getScreenWalkthrough = (workspaceId, programName) =>
  api.get(`${_wsPath(workspaceId)}/screen-walkthroughs/${encodeURIComponent(programName)}`).then((r) => r.data);
export const rebuildScreenWalkthroughs = (workspaceId) =>
  api.post(`${_wsPath(workspaceId)}/screen-walkthroughs/rebuild`).then((r) => r.data);

